import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { CalendarX, RefreshCw, Search, X, TrendingDown, Clock, UserX } from 'lucide-react';
import { toast } from 'sonner';

import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../utils/apiConfig';

const TYPE_META = {
  platform: { label: 'Platform', pill: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  manual: { label: 'Manual', pill: 'bg-violet-100 text-violet-700 border-violet-200' },
};

const HOURS_OPTIONS = [
  { value: 'all', label: 'Any notice' },
  { value: 'lt2', label: '< 2 hrs before' },
  { value: '2to6', label: '2–6 hrs before' },
  { value: '6to24', label: '6–24 hrs before' },
  { value: 'gt24', label: '> 24 hrs before' },
];

const WINDOW_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_7', label: 'Last 7 days' },
  { value: 'last_30', label: 'Last 30 days' },
  { value: 'last_90', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
  { value: 'custom', label: 'Custom date' },
];

const fmtDate = (d) => {
  if (!d) return '—';
  try { return format(new Date(d), 'dd-MMM-yyyy'); } catch { return d; }
};

const fmtAt = (iso) => {
  if (!iso) return '—';
  try { return format(new Date(iso), 'dd-MMM HH:mm'); } catch { return iso; }
};

const fmtHoursBefore = (h) => {
  if (h === null || h === undefined) return '—';
  if (h < 0) return `${Math.abs(Math.round(h))}h after`;
  if (h < 1) return `${Math.round(h * 60)}m before`;
  if (h < 48) return `${h.toFixed(1)}h before`;
  return `${Math.round(h / 24)}d before`;
};

const windowRange = (win, customFrom, customTo) => {
  const fmt = (d) => format(d, 'yyyy-MM-dd');
  const today = new Date();
  const clone = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  switch (win) {
    case 'today': { const t = fmt(clone(today)); return { start_date: t, end_date: t }; }
    case 'yesterday': { const d = clone(today); d.setDate(d.getDate() - 1); const s = fmt(d); return { start_date: s, end_date: s }; }
    case 'tomorrow': { const d = clone(today); d.setDate(d.getDate() + 1); const s = fmt(d); return { start_date: s, end_date: s }; }
    case 'this_month': {
      const s = new Date(today.getFullYear(), today.getMonth(), 1);
      const e = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { start_date: fmt(s), end_date: fmt(e) };
    }
    case 'last_7': { const d = clone(today); d.setDate(d.getDate() - 7); return { start_date: fmt(d), end_date: '' }; }
    case 'last_30': { const d = clone(today); d.setDate(d.getDate() - 30); return { start_date: fmt(d), end_date: '' }; }
    case 'last_90': { const d = clone(today); d.setDate(d.getDate() - 90); return { start_date: fmt(d), end_date: '' }; }
    case 'custom': return { start_date: customFrom || '', end_date: customTo || '' };
    default: return { start_date: '', end_date: '' };
  }
};

export default function CancellationsPage() {
  const { token } = useAuth();

  const [windowSel, setWindowSel] = useState('last_30');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [venueFilter, setVenueFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [hoursFilter, setHoursFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    const { start_date, end_date } = windowRange(windowSel, customFrom, customTo);
    const baseParams = { venue: venueFilter };
    if (start_date) baseParams.start_date = start_date;
    if (end_date) baseParams.end_date = end_date;
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [listRes, anaRes] = await Promise.all([
        axios.get(`${API}/cancellations`, {
          params: {
            ...baseParams,
            platform: platformFilter,
            cancellation_type: typeFilter,
            hours_before: hoursFilter,
            search: search.trim(),
            limit: 500,
          },
          headers,
        }),
        axios.get(`${API}/cancellations/analytics`, { params: baseParams, headers }),
      ]);
      setData(listRes.data);
      setAnalytics(anaRes.data);
    } catch (err) {
      console.error('Cancellations fetch error', err);
      toast.error('Failed to load cancellations');
    } finally {
      setLoading(false);
    }
  }, [windowSel, customFrom, customTo, venueFilter, platformFilter, typeFilter, hoursFilter, search, token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const summary = data?.summary || { total: 0, platform: 0, manual: 0, amount: 0 };
  const rows = data?.cancellations || [];
  const platforms = data?.platforms || [];
  const venues = ['MAKS Pickleball - ISF', 'Sports Complex', 'Bhayandar'];

  const rateRows = analytics?.cancellation_rate_per_platform || [];
  const peakTimings = analytics?.peak_cancellation_timings || [];
  const peakSlot = analytics?.peak_slot;
  const frequentUsers = analytics?.frequent_users || [];
  const maxPeak = Math.max(1, ...peakTimings.map((p) => p.count));

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="cancellations-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900 tracking-tight flex items-center gap-2">
              <CalendarX className="w-6 h-6 sm:w-7 sm:h-7 text-rose-500" />
              Cancellations
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-1">
              Court-booking cancellations — from online platforms (Khelomore / District / Hudle) and manual admin cancellations.
            </p>
          </div>
          <Button onClick={fetchAll} disabled={loading} variant="outline" data-testid="cancellations-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="p-3 sm:p-4" data-testid="cancellations-summary-total">
            <p className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500">Total</p>
            <p className="text-xl sm:text-2xl font-bold text-slate-900 mt-1">{summary.total}</p>
          </Card>
          <Card className="p-3 sm:p-4 border-cyan-200 bg-cyan-50/40" data-testid="cancellations-summary-platform">
            <p className="text-[10px] sm:text-xs uppercase tracking-wider text-cyan-700">Platform</p>
            <p className="text-xl sm:text-2xl font-bold text-cyan-700 mt-1">{summary.platform}</p>
          </Card>
          <Card className="p-3 sm:p-4 border-violet-200 bg-violet-50/40" data-testid="cancellations-summary-manual">
            <p className="text-[10px] sm:text-xs uppercase tracking-wider text-violet-700">Manual</p>
            <p className="text-xl sm:text-2xl font-bold text-violet-700 mt-1">{summary.manual}</p>
          </Card>
          <Card className="p-3 sm:p-4 border-rose-200 bg-rose-50/40" data-testid="cancellations-summary-amount">
            <p className="text-[10px] sm:text-xs uppercase tracking-wider text-rose-700">Value Lost</p>
            <p className="text-xl sm:text-2xl font-bold text-rose-700 mt-1">₹{Math.round(summary.amount).toLocaleString('en-IN')}</p>
          </Card>
        </div>

        {/* Analytics row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* Cancellation rate per platform */}
          <Card className="p-4" data-testid="cancellations-rate-card">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-rose-500" /> Cancellation Rate by Platform
            </h3>
            {rateRows.length === 0 ? (
              <p className="text-xs text-slate-400">No data for this window.</p>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-400 text-left">
                    <th className="pb-2 font-medium">Platform</th>
                    <th className="pb-2 text-right font-medium">Cancelled</th>
                    <th className="pb-2 text-right font-medium">Total</th>
                    <th className="pb-2 text-right font-medium">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {rateRows.map((r) => (
                    <tr key={r.platform} className="border-t border-slate-100">
                      <td className="py-1.5 text-slate-700">{r.platform}</td>
                      <td className="py-1.5 text-right text-rose-600 font-semibold">{r.cancelled}</td>
                      <td className="py-1.5 text-right text-slate-500">{r.total}</td>
                      <td className="py-1.5 text-right font-semibold text-slate-800">{r.rate_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          {/* Peak cancellation timings */}
          <Card className="p-4" data-testid="cancellations-peak-card">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-500" /> Peak Cancellation Timings
            </h3>
            {peakSlot && (
              <p className="text-xs text-slate-500 mb-2">
                Busiest: <span className="font-semibold text-slate-800">{peakSlot.slot}</span> ({peakSlot.count})
              </p>
            )}
            <div className="space-y-1.5">
              {peakTimings.map((p) => (
                <div key={p.slot} className="flex items-center gap-2">
                  <span className="text-[11px] text-slate-500 w-24 shrink-0">{p.slot}</span>
                  <div className="flex-1 bg-slate-100 rounded h-3 overflow-hidden">
                    <div
                      className="h-full bg-amber-400 rounded"
                      style={{ width: `${(p.count / maxPeak) * 100}%` }}
                    />
                  </div>
                  <span className="text-[11px] font-semibold text-slate-700 w-6 text-right">{p.count}</span>
                </div>
              ))}
            </div>
          </Card>

          {/* Frequent cancelling users */}
          <Card className="p-4" data-testid="cancellations-frequent-card">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <UserX className="w-4 h-4 text-rose-500" /> Frequent Cancelling Users
            </h3>
            {frequentUsers.length === 0 ? (
              <p className="text-xs text-slate-400">No data for this window.</p>
            ) : (
              <div className="space-y-1.5">
                {frequentUsers.map((u) => (
                  <div key={u.player_mobile} className="flex items-center justify-between text-xs">
                    <div className="truncate">
                      <span className="text-slate-800 font-medium">{u.player_name}</span>
                      <span className="text-slate-400 ml-1 font-mono">{u.player_mobile}</span>
                    </div>
                    <span className="shrink-0 inline-flex items-center justify-center min-w-[1.5rem] px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 font-semibold">
                      {u.count}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-4" data-testid="cancellations-filters">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Window</label>
              <Select value={windowSel} onValueChange={setWindowSel}>
                <SelectTrigger data-testid="cancellations-days-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WINDOW_OPTIONS.map((d) => (
                    <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {windowSel === 'custom' && (
              <>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5 block">From</label>
                  <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} data-testid="cancellations-custom-from" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5 block">To</label>
                  <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} data-testid="cancellations-custom-to" />
                </div>
              </>
            )}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Venue</label>
              <Select value={venueFilter} onValueChange={setVenueFilter}>
                <SelectTrigger data-testid="cancellations-venue-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All venues</SelectItem>
                  {venues.map((v) => (<SelectItem key={v} value={v}>{v}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Platform</label>
              <Select value={platformFilter} onValueChange={setPlatformFilter}>
                <SelectTrigger data-testid="cancellations-platform-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All platforms</SelectItem>
                  {platforms.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Type</label>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger data-testid="cancellations-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="platform">Platform</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Notice</label>
              <Select value={hoursFilter} onValueChange={setHoursFilter}>
                <SelectTrigger data-testid="cancellations-hours-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {HOURS_OPTIONS.map((h) => (
                    <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Search name / mobile</label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <Input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="name / mobile / id"
                  className="pl-9 pr-9"
                  data-testid="cancellations-search-input"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    data-testid="cancellations-clear-search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Table */}
        <Card className="overflow-visible">
          {loading ? (
            <div className="p-12 text-center text-slate-500">Loading cancellations…</div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-slate-500" data-testid="cancellations-empty">
              No cancellations match the current filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="cancellations-table">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Cancelled At</th>
                    <th className="px-3 py-3 text-left font-semibold">Player</th>
                    <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Mobile</th>
                    <th className="px-3 py-3 text-left font-semibold">Venue / Court</th>
                    <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Slot</th>
                    <th className="px-3 py-3 text-left font-semibold">Platform</th>
                    <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Booking ID</th>
                    <th className="px-3 py-3 text-center font-semibold">Type</th>
                    <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Notice</th>
                    <th className="px-3 py-3 text-left font-semibold">By</th>
                    <th className="px-3 py-3 text-right font-semibold">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const meta = TYPE_META[r.cancellation_type] || TYPE_META.manual;
                    return (
                      <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`cancellation-row-${r.id}`}>
                        <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">{fmtAt(r.cancelled_at)}</td>
                        <td className="px-3 py-2.5 text-slate-900 font-medium">{r.player_name || '—'}</td>
                        <td className="px-3 py-2.5 text-slate-600 font-mono text-xs whitespace-nowrap">{r.player_mobile || '—'}</td>
                        <td className="px-3 py-2.5 text-slate-700">
                          {r.court || '—'}
                          <span className="block text-[11px] text-slate-400">{r.venue}</span>
                        </td>
                        <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap text-xs">
                          {fmtDate(r.date)}
                          <span className="block text-slate-400">{r.start_time} - {r.end_time}</span>
                        </td>
                        <td className="px-3 py-2.5 text-slate-700">{r.booking_source || '—'}</td>
                        <td className="px-3 py-2.5 text-slate-600 font-mono text-xs whitespace-nowrap">
                          {r.platform_booking_id || r.booking_id || '—'}
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium ${meta.pill}`}>
                            {meta.label}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-slate-600 text-xs whitespace-nowrap">{fmtHoursBefore(r.hours_before_schedule)}</td>
                        <td className="px-3 py-2.5 text-slate-600 text-xs">{r.cancelled_by || '—'}</td>
                        <td className="px-3 py-2.5 text-right text-slate-700">{r.amount ? `₹${Math.round(r.amount).toLocaleString('en-IN')}` : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!loading && rows.length > 0 && (
            <p className="px-3 py-2 text-xs text-slate-400 text-right">
              Showing {rows.length} cancellation{rows.length === 1 ? '' : 's'} — refine filters to narrow.
            </p>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
}
